import SubmitButton from '../forms/SubmitButton'
import TextInput from '../forms/TextInput'
import type {ReactElement} from 'react'
import {Dialog, Transition} from '@headlessui/react'

import {PhoneNumberUtil, PhoneNumberFormat} from 'google-libphonenumber'
import {Fragment} from 'react'
import React from 'react'
import {Formik, Form, FormikHelpers} from 'formik'
import {useRouter} from 'next/router'
import toast from 'react-hot-toast'
import CustomToast from '../CustomToast'
import {ForgotValues, NewPassSchema, NewPassValues} from '../../forms/Forgot'
import {useMutation} from 'react-query'
import {
  ForgotPassword,
  ForgotPasswordEmail,
  PasswordReset,
} from '@/services/api/auth'
import {VerificationDataOptional} from '@/types'
import ForgotLoginPhoneForm from './ForgotLoginPhoneForm'
import BackButton from '../BackButton'
import alerts from '@/config/alerts.json'
type Props = {
  isOpen: boolean
  setIsOpen: (arg0: boolean) => void
}

export default function ForgotModal({isOpen, setIsOpen}: Props): ReactElement {
  const closeModal = () => {
    setTimeout(() => {
      setIsOpen(false)
    }, 500)
  }
  const mutation = useMutation(ForgotPassword)
  const Emailmutation = useMutation(ForgotPasswordEmail)
  const passMutation = useMutation(PasswordReset)
  const [tab, setTab] = React.useState<'forgot' | 'new-pass'>('forgot')
  const [forgotData, setForgotData] = React.useState<
    VerificationDataOptional | undefined
  >()

  const [seconds, setSeconds] = React.useState(60)

  const [iso2, setIso2] = React.useState('IL')
  React.useEffect(() => {
    const countdown = setInterval(() => {
      setSeconds(prevSeconds => prevSeconds - 1)
    }, 1000)

    if (seconds === 0) {
      clearInterval(countdown)
    }

    return () => clearInterval(countdown)
  }, [seconds])

  const [message, setMessage] = React.useState(
    'أدخل الرمز المرسل إلى بريدك الإلكتروني',
  )

  const handleSubmitPhone = (
    values: ForgotValues,
    actions: FormikHelpers<ForgotValues>,
  ) => {
    const phoneUtil = PhoneNumberUtil.getInstance()
    const parsedNumber = phoneUtil.parseAndKeepRawInput(values.phone, iso2)
    const isValid = phoneUtil.isValidNumber(parsedNumber)

    if (!isValid) {
      actions.setFieldError('phone', 'Phone Number is Invalid')
      return
    }
    mutation.mutate(
      {phone: values.phone, country_code: values.country_code},
      {
        onSuccess(data: any) {
          toast.custom((t) => <CustomToast t={t} message={alerts.verification_code_phone} type="success" />, {
            duration: 2500,
          });
          setMessage('أدخل الرمز المرسل إلى هاتفك')
          setForgotData({
            phone: {
              number: values.phone,
              country_code: values.country_code,
            },
          })
          setSeconds(60)
          setTab('new-pass')
        },
        onError(error: any) {
          if (error.response.status === 500) {
            actions.setFieldError('email', 'An Error Occurred')
            toast.custom((t) => <CustomToast t={t} message={'Error Occurred'} type="error" />, {
              duration: 4000,
            });
          } else {
            toast.custom((t) => <CustomToast t={t} message={error.response.data.message} type="error" />, {
              duration: 4000,
            });
          }
        },
      },
    )
  }

  const handleResendEmail = (phone: any) => {
    Emailmutation.mutate(
      {
        country_code: phone.country_code,
        phone: phone.number,
      },
      {
        onSuccess() {
          setSeconds(60)
          toast.custom((t) => <CustomToast t={t} message={alerts.forgot_code} type="success" />, {
              duration: 4000,
          });
        },
        onError(error: any) {
          toast.custom((t) => <CustomToast t={t} message={error.response.data.message} type="error" />, {
              duration: 4000,
            id: 'Validation Error',
          });
        },
      },
    )
  }
  const handleResendPhone = (phone: any) => {
    mutation.mutate(
      {
        country_code: phone.country_code,
        phone: phone.number,
      },
      {
        onSuccess() {
          setSeconds(60)
          toast.custom((t) => <CustomToast t={t} message={alerts.forgot_code} type="success" />, {
              duration: 4000,
          });
        },
        onError(error: any) {
          toast.custom((t) => <CustomToast t={t} message={error.response.data.message} type="error" />, {
              duration: 4000,
              id: 'Validation Error',
          });
        },
      },
    )
  }
  const handleSubmitNew = (
    values: NewPassValues,
    actions: FormikHelpers<NewPassValues>,
  ) => {
    passMutation.mutate(
      {
        forgot_code: values.code,
        password: values.password,
        phone: forgotData?.phone?.number ?? '',
        country_code: forgotData?.phone?.country_code ?? '',
      },
      {
        onSuccess(data: any) {
          setTimeout(() => {
            setIsOpen(false)
          }, 500)
          setTab('forgot')
          toast.custom((t) => <CustomToast t={t} message={alerts.password_reset_success} type="success" />, {
              duration: 4000,
          });
        },
        onError(error: any) {
          if (error.response.status === 404) {
            actions.setFieldError('code', alerts.code_failure)
            toast.custom((t) => <CustomToast t={t} message={alerts.code_failure} type="error" />, {
              duration: 4000,
            });
          } else {
            toast.custom((t) => <CustomToast t={t} message={error.response.data.message} type="error" />, {
              duration: 4000,
            });
          }
        },
      },
    )
  }
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-md xl:max-w-2xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                <Dialog.Title className="text-lg font-medium px-4 pt-4">
                  <div className="flex justify-end items-end">
                    <a
                      href="#"
                      onClick={e => {
                        e.preventDefault()
                        setTimeout(() => {
                          setIsOpen(false)
                        }, 500)
                      }}
                      className="close-icon border rounded-full p-2 self-end cursor-pointer">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 21 21"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M11.9109 10.2326L20.201 1.94261C20.3648 1.75131 20.4504 1.50523 20.4407 1.25355C20.4309 1.00188 20.3266 0.763139 20.1485 0.585044C19.9704 0.40695 19.7317 0.302617 19.48 0.292896C19.2283 0.283175 18.9823 0.368781 18.791 0.532608L10.5009 8.82261L2.21095 0.522607C2.02265 0.334304 1.76725 0.228516 1.50095 0.228516C1.23465 0.228516 0.979253 0.334304 0.79095 0.522607C0.602646 0.710911 0.496858 0.966306 0.496858 1.23261C0.496858 1.49891 0.602646 1.7543 0.79095 1.94261L9.09095 10.2326L0.79095 18.5226C0.686268 18.6123 0.601248 18.7226 0.541224 18.8466C0.481201 18.9707 0.447471 19.1058 0.442151 19.2436C0.436832 19.3813 0.460037 19.5186 0.510312 19.6469C0.560587 19.7753 0.636845 19.8918 0.7343 19.9893C0.831754 20.0867 0.948302 20.163 1.07663 20.2132C1.20495 20.2635 1.34228 20.2867 1.48 20.2814C1.61772 20.2761 1.75285 20.2424 1.87692 20.1823C2.00098 20.1223 2.1113 20.0373 2.20095 19.9326L10.5009 11.6426L18.791 19.9326C18.9823 20.0964 19.2283 20.182 19.48 20.1723C19.7317 20.1626 19.9704 20.0583 20.1485 19.8802C20.3266 19.7021 20.4309 19.4633 20.4407 19.2117C20.4504 18.96 20.3648 18.7139 20.201 18.5226L11.9109 10.2326Z"
                          fill="gray"
                        />
                      </svg>
                    </a>
                  </div>
                </Dialog.Title>
                <div className="modal-content p-3 lg:p-6 xl:p-8 2xl:p-11">
                  {tab === 'forgot' && (
                    <div className="mb-6">
                      <>
                        <div>
                          <p className="font-bold text-right">
                            الرجاء إدخال رقم هاتفك.
                          </p>
                        </div>
                        <ForgotLoginPhoneForm
                          handleSubmitPhone={handleSubmitPhone}
                          mutation={mutation}
                          setIso2={setIso2}
                        />
                      </>
                    </div>
                  )}

                  {tab === 'new-pass' && (
                    <div className="mb-6">
                      <>
                        <BackButton onClick={() => setTab('forgot')} />
                        <div className="mt-14 lg:mt-6 xl:mt-4">
                          <p className="font-bold text-right">{message}</p>
                        </div>
                        <Formik
                          initialValues={{
                            password: '',
                            code: '',
                          }}
                          validationSchema={NewPassSchema}
                          onSubmit={handleSubmitNew}>
                          {({errors, touched}) => (
                            <Form>
                              <div className="form mt-4">
                                <div className="form-group flex flex-col">
                                  <TextInput
                                    label={'رمز عبور*'}
                                    type="text"
                                    name="code"
                                    placeholder={
                                      'أدخل الرمز المرسل إلى بريدك الإلكتروني'
                                    }
                                    isError={errors.code && touched.code}
                                    error={errors.code}
                                  />

                                  {!mutation.isLoading ? (
                                    <div className="text-right text-sm">
                                      <a
                                        href="#"
                                        onClick={e => {
                                          e.preventDefault()
                                          if (seconds == 0) {
                                            handleResendPhone({
                                              number: forgotData?.phone?.number,
                                              country_code:
                                                forgotData?.phone?.country_code,
                                            })
                                          }
                                        }}
                                        className={`block ${
                                          seconds === 0
                                            ? 'text-black'
                                            : 'text-gray-500'
                                        }`}>
                                        أعد إرسال الرمز إلى الهاتف ({seconds}s)
                                      </a>

                                      <a
                                        href="#"
                                        onClick={e => {
                                          e.preventDefault()
                                          if (seconds == 0) {
                                            handleResendEmail({
                                              number: forgotData?.phone?.number,
                                              country_code:
                                                forgotData?.phone?.country_code,
                                            })
                                          }
                                        }}
                                        className={`block mt-1 ${
                                          seconds === 0
                                            ? 'text-black'
                                            : 'text-gray-500'
                                        }`}>
                                        أعد إرسال الرمز إلى البريد الإلكتروني (
                                        {seconds}s)
                                      </a>
                                    </div>
                                  ) : (
                                    <svg
                                      className="animate-spin ml-1 mr-3 h-5 w-5 text-primary"
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24">
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"></circle>
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                  )}
                                </div>
                                <div className="form-group mt-4 flex flex-col">
                                  <TextInput
                                    label={'كلمة السر الجديدة*'}
                                    type="password"
                                    name="password"
                                    placeholder={'أدخل كلمة المرور الجديدة'}
                                    isError={
                                      errors.password && touched.password
                                    }
                                    error={errors.password}
                                  />
                                </div>
                                <SubmitButton
                                  isLoading={passMutation.isLoading}
                                  text={'حفظ كلمة المرور'}
                                />
                              </div>
                            </Form>
                          )}
                        </Formik>
                      </>
                    </div>
                  )}

                  {/*
                   *<p className="text-center mt-6 text-xs">
                   *  ليس لديك حساب؟
                   *  <a
                   *    href="#"
                   *    onClick={() => {
                   *      setIsOpen(false)
                   *      setIsLoginOpen(true)
                   *    }}
                   *    data-test="register-page"
                   *    className="text-primary underline font-bold mr-1 mt-4">
                   *    سجل حساب جديد
                   *  </a>
                   *</p>
                   */}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
